/**
 * useWalletConnect — WalletConnect v2 bridge for TON
 *
 * Enables institutional desktop wallets (Ledger Live via WC, Safe, etc.) to
 * connect to Whether.market via WalletConnect v2 QR pairing.
 *
 * TON doesn't have a native WC2 adapter at the time of writing, so we use
 * the bridge pattern:
 *   1. Create a WC2 SignClient session
 *   2. Expose the pairing URI (QR code)
 *   3. On session approval the wallet provides its TON address
 *   4. Transactions are signed via wc_sessionRequest (TON-specific RPC)
 *
 * Reference chain/method names follow the TIP-0 draft:
 *   chain:  "ton:mainnet" or "ton:testnet"
 *   method: "ton_sendTransaction"
 */

import { useState, useCallback, useRef } from 'react';
import { useWalletStore } from '../store/wallet';

// WalletConnect Project ID — use your own from https://cloud.walletconnect.com
// This is a placeholder; replace with actual project ID for production
const WC_PROJECT_ID = import.meta.env.VITE_WC_PROJECT_ID || 'whether-market-placeholder';
const TON_CHAIN = 'ton:testnet';
const TON_SEND_TX_METHOD = 'ton_sendTransaction';
const TON_GET_ACCOUNTS_METHOD = 'ton_getAccounts';

export interface WcTransaction {
  to: string;
  value: string; // nanoTON as string
  payload?: string; // base64 cell
  stateInit?: string;
}

interface UseWalletConnectReturn {
  uri: string | null;
  isConnecting: boolean;
  isConnected: boolean;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  sendTransaction: (tx: WcTransaction) => Promise<string | null>;
}

// Lazy-load SignClient to avoid bundle bloat when not used
async function getSignClient() {
  const { default: SignClient } = await import('@walletconnect/sign-client');
  return SignClient;
}

let signClientInstance: Awaited<ReturnType<typeof getSignClient>> extends new (...a: any[]) => infer R ? R : never | null = null as any;

export function useWalletConnect(): UseWalletConnectReturn {
  const [uri, setUri] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sessionTopicRef = useRef<string | null>(null);

  const { address, wcSessionTopic, setAddress, setWcUri, setWcSession, disconnect: storeDisconnect } = useWalletStore();

  const connect = useCallback(async () => {
    setIsConnecting(true);
    setError(null);
    setUri(null);

    try {
      const SignClient = await getSignClient();

      // Init client if needed
      if (!signClientInstance) {
        signClientInstance = await SignClient.init({
          projectId: WC_PROJECT_ID,
          metadata: {
            name: 'Whether.market',
            description: 'Weather-based perpetual derivatives',
            url: 'https://whether.market',
            icons: ['https://whether.market/logo.png'],
          },
        });
      }

      const client = signClientInstance;

      // Check if we already have a session
      const sessions = client.session.getAll();
      if (sessions.length > 0 && wcSessionTopic) {
        const existing = sessions.find((s: { topic: string }) => s.topic === wcSessionTopic);
        if (existing) {
          // Restore session — extract address
          const accounts = existing.namespaces?.['ton']?.accounts || [];
          const firstAccount = accounts[0]; // "ton:testnet:EQ..."
          if (firstAccount) {
            const addr = firstAccount.split(':')[2];
            setAddress(addr, 'walletconnect');
            sessionTopicRef.current = existing.topic;
            setIsConnecting(false);
            return;
          }
        }
      }

      // Create new pairing
      const { uri: pairingUri, approval } = await client.connect({
        requiredNamespaces: {
          ton: {
            methods: [TON_SEND_TX_METHOD, TON_GET_ACCOUNTS_METHOD],
            chains: [TON_CHAIN],
            events: ['accountsChanged', 'chainChanged'],
          },
        },
      });

      if (pairingUri) {
        setUri(pairingUri);
        setWcUri(pairingUri);
      }

      // Wait for wallet to approve (user scans QR)
      const session = await approval();
      sessionTopicRef.current = session.topic;
      setWcSession(session.topic);

      // Extract TON address from session
      const accounts = session.namespaces?.['ton']?.accounts || [];
      const firstAccount = accounts[0];
      if (firstAccount) {
        const addr = firstAccount.split(':')[2];
        setAddress(addr, 'walletconnect');
      } else {
        throw new Error('No TON account in WalletConnect session');
      }

      setUri(null);
      setWcUri(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // User cancelled — not a real error
      if (!msg.includes('User rejected') && !msg.includes('cancelled')) {
        setError(msg);
      }
    } finally {
      setIsConnecting(false);
    }
  }, [wcSessionTopic, setAddress, setWcUri, setWcSession]);

  const disconnect = useCallback(async () => {
    const topic = sessionTopicRef.current || wcSessionTopic;
    if (signClientInstance && topic) {
      try {
        await signClientInstance.disconnect({
          topic,
          reason: { code: 6000, message: 'User disconnected' },
        });
      } catch {
        // Ignore disconnect errors
      }
    }
    sessionTopicRef.current = null;
    storeDisconnect();
  }, [wcSessionTopic, storeDisconnect]);

  const sendTransaction = useCallback(async (tx: WcTransaction): Promise<string | null> => {
    const topic = sessionTopicRef.current || wcSessionTopic;
    if (!signClientInstance || !topic) {
      setError('No active WalletConnect session');
      return null;
    }

    try {
      const result = await signClientInstance.request({
        topic,
        chainId: TON_CHAIN,
        request: {
          method: TON_SEND_TX_METHOD,
          params: {
            messages: [
              {
                address: tx.to,
                amount: tx.value,
                payload: tx.payload,
                stateInit: tx.stateInit,
              },
            ],
            valid_until: Math.floor(Date.now() / 1000) + 300,
          },
        },
      });

      // result should contain boc or txHash
      return (result as any)?.boc || (result as any)?.txHash || null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      return null;
    }
  }, [wcSessionTopic]);

  return {
    uri,
    isConnecting,
    isConnected: !!address && useWalletStore.getState().walletType === 'walletconnect',
    error,
    connect,
    disconnect,
    sendTransaction,
  };
}
